import React, {useEffect, useId, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {
  DashboardButton,
  DashboardIconButton,
} from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import type {DashboardConfirmationModalProps} from "@/ReactInit/Scripts/DashboardPage/types";
import {cn} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";

type AlertStep = "confirm" | "success";

const ANIM_MS = 180;

export const DashboardConfirmationModal = (
  {
    isOpen,
    type = "confirmation",
    title,
    description,
    confirmLabel,
    cancelLabel,
    onConfirm,
    onCancel,
    confirmDisabled,
    cancelDisabled,
    isSuccess,
    successTitle,
    successButtonLabel = "Ok",
    onSuccessClose,
    rememberKey,
    rememberLabel = "Ikke vis denne meldingen neste gang",
  }: DashboardConfirmationModalProps) => {
  const titleId = useId();
  const descriptionId = useId();

  const [present, setPresent] = useState(false);
  const [openUi, setOpenUi] = useState(false);

  const [alertStep, setAlertStep] = useState<AlertStep>("confirm");
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isHandlingConfirm, setIsHandlingConfirm] = useState(false);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

  const isAlert = type === "alert";
  const isConfirmation = type === "confirmation";
  const canRemember = isConfirmation && !!rememberKey;

  const isSuccessStep = isAlert && alertStep === "success";

  useEffect(() => {
    if (isOpen) {
      previousActiveElementRef.current =
        (document.activeElement as HTMLElement) ?? null;

      setPresent(true);
      setAlertStep("confirm");
      setDontShowAgain(false);
      setIsHandlingConfirm(false);
      setOpenUi(false);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => setOpenUi(true));
      });

      return;
    }

    if (!isOpen && present) {
      setOpenUi(false);
      const t = window.setTimeout(() => {
        setPresent(false);
        previousActiveElementRef.current?.focus?.();
      }, ANIM_MS);

      return () => window.clearTimeout(t);
    }
  }, [isOpen, present]);

  useEffect(() => {
    if (isAlert && isSuccess) setAlertStep("success");
  }, [isAlert, isSuccess]);

  useEffect(() => {
    if (openUi && confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, [openUi, alertStep]);

  useEffect(() => {
    if (!present) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      if (isSuccessStep) {
        onSuccessClose?.();
        return;
      }

      if (!cancelDisabled) onCancel();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [present, onCancel, cancelDisabled, isSuccessStep, onSuccessClose]);

  useEffect(() => {
    if (!present) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [present]);

  useLayoutEffect(() => {
    if (!present) return;

    const el = measureRef.current;
    if (!el) return;

    const measure = () => {
      const next = Math.ceil(el.scrollHeight);
      setMeasuredHeight(next);
    };

    measure();
    requestAnimationFrame(() => measure());
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [
    present,
    alertStep,
    isSuccessStep,
    title,
    successTitle,
    description,
    canRemember,
    dontShowAgain,
    confirmLabel,
    cancelLabel,
    successButtonLabel,
  ]);

  if (!present) return null;

  const handleBackdropClick = () => {
    if (isSuccessStep) return onSuccessClose?.();
    if (!cancelDisabled) onCancel();
  };

  const handleCloseClick = () => {
    if (isSuccessStep) return onSuccessClose?.();
    if (!cancelDisabled) onCancel();
  };

  const handleConfirmClick = async () => {
    if (confirmDisabled) return;

    if (!canRemember || !dontShowAgain) {
      await onConfirm();
      return;
    }

    try {
      setIsHandlingConfirm(true);
      await onConfirm();

      try {
        localStorage.setItem(rememberKey!, "1");
      } catch {
        // ignore storage errors
      }
    } finally {
      setIsHandlingConfirm(false);
    }
  };

  const effectiveConfirmDisabled = !!confirmDisabled || isHandlingConfirm;
  const effectiveCancelDisabled = !!cancelDisabled || isHandlingConfirm;

  const modal = (
    <div
      className={cn(
        "fixed inset-0 z-[1000] flex items-center justify-center px-4",
        "transition-opacity",
        openUi ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
      )}
      style={{transitionDuration: `${ANIM_MS}ms`}}
    >
      <div
        className={cn(
          "absolute inset-0 bg-black transition-opacity",
          openUi ? "opacity-50" : "opacity-0",
        )}
        style={{transitionDuration: `${ANIM_MS}ms`}}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={!isSuccessStep && description ? descriptionId : undefined}
        className={cn(
          "relative z-[1001] w-full max-w-[525px] rounded-lg bg-white shadow-md overflow-hidden",
          "transition-[transform,opacity] will-change-transform",
          openUi
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 translate-y-2 scale-[0.98]",
        )}
        style={{transitionDuration: `${ANIM_MS}ms`}}
      >
        <div
          style={{
            height: measuredHeight ?? undefined,
            transition: `height ${ANIM_MS}ms ease`,
          }}
        >
          <div ref={measureRef}>
            <div className="flex justify-end h-12">
              <DashboardIconButton
                label="Lukk"
                variant="simple"
                icon="icon-x"
                rounded="medium"
                size="3.5"
                onClick={handleCloseClick}
              />
            </div>
            <div className="px-7 pb-7">
              <div className="flex flex-col gap-5 tablet:gap-10">
                <div className="flex flex-col gap-4">
                  {isAlert && !isSuccessStep && (
                    <div
                      className="flex align-middle justify-center h-10 w-10 text-[40px] leading-[40px] text-red-secondary">
                      <span className="icon-triangle-alert" aria-hidden="true"/>
                    </div>
                  )}
                  <h2 id={titleId} className="text-2xl leading-8 font-semibold text-black">
                    {isSuccessStep ? (successTitle ?? title) : title}
                  </h2>
                  {!isSuccessStep && description && (
                    <p id={descriptionId} className="text-base leading-6 text-black">
                      {description}
                    </p>
                  )}
                </div>
                {!isSuccessStep && canRemember && (
                  <label
                    className={cn(
                      "flex items-center gap-3 select-none",
                      effectiveConfirmDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                    )}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={dontShowAgain}
                      onChange={(e) => setDontShowAgain(e.target.checked)}
                      disabled={effectiveConfirmDisabled}
                    />
                    <span className="dropdown-checkbox" aria-hidden="true">
                      <span
                        className={cn(
                          "dropdown-checkbox-box",
                          dontShowAgain && "dropdown-checkbox-checked",
                        )}
                      />
                    </span>
                    <span className="text-base leading-6 text-black">{rememberLabel}</span>
                  </label>
                )}
                <div className="flex flex-col justify-center gap-5">
                  {isSuccessStep ? (
                    <DashboardButton
                      label={successButtonLabel}
                      alignText="center"
                      variant="darkplain"
                      rounded="full"
                      className="py-3 px-6"
                      onClick={() => onSuccessClose?.()}
                      ref={confirmButtonRef as never}
                    />
                  ) : (
                    <>
                      <DashboardButton
                        label={confirmLabel}
                        alignText="center"
                        variant={type === "alert" ? "alert" : "darkplain"}
                        onClick={handleConfirmClick}
                        icon={type === "alert" ? "icon-trash" : "icon-check"}
                        disabled={effectiveConfirmDisabled}
                        className="py-3 px-6"
                        rounded="full"
                        ref={confirmButtonRef as never}
                      />
                      <DashboardButton
                        label={cancelLabel}
                        alignText="center"
                        icon="icon-circle-arrow-left"
                        variant="sidebar"
                        rounded="full"
                        className="py-3 px-6"
                        onClick={onCancel}
                        disabled={effectiveCancelDisabled}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
